import { useState } from 'react';
import { useLang } from '../../i18n/translations';
import { cn } from '../../lib/utils';

const IMAGES = [
  '/new_assets/ONBD1.webp',
  '/new_assets/ONBD2.webp',
  '/new_assets/ONBD3.webp',
];

interface Props {
  onDone: () => void;
}

export function OnboardingFlow({ onDone }: Props) {
  const [step, setStep] = useState(0);
  const { t } = useLang();

  const screens = t.onboarding.screens;
  const isLast = step === screens.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">

      {/* Backdrop — desktop only */}
      <div className="hidden md:block absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Card — full screen mobile, centered modal desktop */}
      <div className="relative z-10 bg-surface-brand flex flex-col
                      w-full h-full
                      md:w-[420px] md:h-auto md:max-h-[90vh] md:rounded-3xl md:overflow-hidden md:shadow-2xl">

      {/* Illustration */}
      <div className="flex-1 flex items-center justify-center px-8 pt-16 pb-4 min-h-0 md:flex-none md:h-[210px] md:pt-8 md:pb-2">
        <img
          key={step}
          src={IMAGES[step]}
          alt={`Onboarding ${step + 1}`}
          className="w-full max-w-xs h-full object-contain drop-shadow-lg"
          style={{ animation: 'fadeSlideUp 0.35s ease-out both' }}
          draggable={false}
        />
      </div>

      {/* Bottom sheet */}
      <div className="bg-white rounded-t-[32px] px-6 pt-6 pb-8 shadow-[0_-4px_24px_rgba(0,0,0,0.06)] md:rounded-none md:rounded-b-3xl md:pt-5 md:pb-6">

        {/* Dots */}
        <div className="flex justify-center gap-2 mb-6">
          {screens.map((_, i) => (
            <span
              key={i}
              className={cn(
                'rounded-full transition-all duration-300',
                i === step ? 'w-6 h-2 bg-brand' : 'w-2 h-2 bg-gray-200',
              )}
            />
          ))}
        </div>

        <h2 className="font-display text-[22px] font-bold text-shell text-center mb-2 leading-snug">
          {screens[step].title}
        </h2>
        <p className="font-display text-sm text-ink-muted text-center leading-relaxed mb-6 whitespace-pre-line">
          {screens[step].body}
        </p>

        <button
          onClick={() => (isLast ? onDone() : setStep(step + 1))}
          className="w-full rounded-2xl bg-brand text-white font-display font-semibold text-[15px]
            flex items-center justify-center transition-opacity active:opacity-80"
          style={{ height: '52px' }}
        >
          {isLast ? t.onboarding.start : t.onboarding.next}
        </button>

        {!isLast && (
          <button
            onClick={onDone}
            className="w-full mt-3 font-display text-sm text-ink-ghost text-center"
          >
            {t.onboarding.skip}
          </button>
        )}
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      </div>{/* end card */}
    </div>
  );
}
